<template>
    <div :id="'kdp-anchor-' + uuid" class="k-date-picker">
        <v-text-field
            v-model="internalDateText"
            :clearable="true"
            :error-messages="modelFormatError"
            :label="label ?? ''"
            :placeholder="displayFormat"
            :rules="[dateTextValidation as any]"
            append-inner-icon="mdi-calendar"
            class="ma-0"
            clear-icon="mdi-close-circle"
            type="text"
            @click:append-inner="showDatePicker = true"
            @click:clear="reset"
        />
    </div>
    <template v-if="showDatePicker">
        <v-overlay
            v-model="showDatePicker"
            :activator="'#kdp-anchor-' + uuid + ' .v-input__control'"
            :close-on-back="false"
            :close-on-content-click="false"
            :open-on-click="false"
            :persistent="false"
            class="k-date-picker"
            location="bottom right"
            location-strategy="connected"
            scroll-strategy="block"
        >
            <v-locale-provider :locale="locale">
                <v-date-picker
                    v-model="internalDateObj as any"
                    :hide-actions="true"
                    :show-adjacent-months="showAdjacentMonths"
                    :show-week="showWeek"
                    header=""
                    input-mode="calendar"
                    title=""
                    view-mode="month"
                >
                    <template v-slot:header/>
                </v-date-picker>
            </v-locale-provider>
        </v-overlay>
    </template>
</template>

<script lang="ts">
import {computed, defineComponent, type PropType, ref, watch} from "vue"
import {intlFormat, isDate, isValid, parse} from "date-fns"
import {format} from "date-fns-tz"
import {VDatePicker} from "vuetify/components";

type ValidFormat = 'js-date' | 'str-date' | 'str-datetime' | 'str-datetime-tz'
type ValidLocale = 'de-AT' | 'en-US'
type ValidModelValue = Date | string | null

export default defineComponent({
    components: {VDatePicker},
    emits: ['update:model-value'],
    props: {
        label: {
            type: String,
        },
        locale: {
            type: String as PropType<ValidLocale>,
            default: 'de-AT',
        },
        modelFormat: {
            type: String as PropType<ValidFormat>,
            default: 'str-datetime'
        },
        modelValue: {
            type: [Date, String, null] as PropType<ValidModelValue>,
            required: true,
        },
        precision: {
            type: Number,
            default: 3,
            validator: (value: number): boolean => {
                return value >= 0 && value <= 9
            },
        },
        showAdjacentMonths: {
            type: Boolean,
            default: true,
        },
        showWeek: {
            type: Boolean,
            default: false,
        },
        stripTz: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, {emit}) {
        const regex = {
            dateTime: /\d{4}(-\d{2}){2}T\d{2}(:\d{2}){2}/,
            timeZone: /((Z)|([A-Z]{3})|([+-]\d(\d(:?\d{2})?)?))/,
        }
        const dateTimeEpoch = '1970-01-01T00:00:00.000Z'

        const internalDateObj = ref<Date | null>(null)
        const internalDateText = ref<string>('')
        const modelFormatError = ref<string>('')
        const showDatePicker = ref<boolean>(false)
        const uuid = ref<string>('')

        const dateTimeRegex = computed(() => {
            return props.precision > 0
                ? new RegExp(regex.dateTime.source + '\\.\\d{' + props.precision + '}')
                : new RegExp(regex.dateTime.source)
        })

        const dateTimeTzRegex = computed(() => {
            return new RegExp(dateTimeRegex.value.source + regex.timeZone.source)
        })

        const displayFormat = computed((): string => {
            return localeDateParsePattern.value.toUpperCase()
        })

        const invalidFormatMessage = computed((): string => {
            switch (props.locale) {
                case 'de-AT':
                    return 'ungültiges Datumsformat'
                case 'en-US':
                    return 'invalid date format'
                default: {
                    const message = 'unsupported locale: ' + props.locale
                    console.error(message)
                    return message
                }
            }
        })

        const localeDateParsePattern = computed((): string => {
            const getPatternForPart = (part: Intl.DateTimeFormatPart) => {
                switch (part.type) {
                    case 'day':
                        return 'dd'
                    case 'month':
                        return 'MM'
                    case 'year':
                        return 'y'.repeat(part.value.length)
                    case 'literal':
                        return part.value
                    default:
                        return ''
                }
            }
            return new Intl.DateTimeFormat(props.locale)
                .formatToParts(new Date(dateTimeEpoch))
                .map(getPatternForPart)
                .join('')
        })

        const dateFromModel = (value: ValidModelValue): Date | null => {
            if (value === null)
                return null
            if (props.modelFormat === 'js-date')
                return typeof value !== 'string' ? value : new Date('invalid')
            if (typeof value !== 'string')
                return new Date('invalid')
            let subject: string
            if (!value.endsWith('.000Z')) {
                value += '.000Z'
            }
            switch (props.modelFormat) {
                case 'str-date':
                    subject = value + 'T00:00:00' + precisionSuffix('0')
                    return dateTimeRegex.value.exec(subject)
                        ? new Date(subject)
                        : new Date('invalid')
                case 'str-datetime':
                    subject = props.stripTz ? stripTz(value) : value
                    return dateTimeRegex.value.exec(subject)
                        ? new Date(subject)
                        : new Date('invalid')
                case 'str-datetime-tz':
                    subject = props.stripTz ? stripTz(value) : value
                    return props.stripTz
                        ? (dateTimeRegex.value.exec(subject)
                            ? new Date(subject)
                            : new Date('invalid'))
                        : (dateTimeTzRegex.value.exec(subject)
                            ? new Date(subject)
                            : new Date('invalid'))
                default:
                    console.error('invalid date picker model-format value')
                    return new Date('invalid')
            }
        }

        const dateTextValidation = (value: any): boolean | string => {
            return (value === '' || value === null)
                || isValid(parseLocaleDateStr(value))
                || invalidFormatMessage.value
        }

        const dateTimeString = (date: Date, withTime: boolean = true, timeZoneSuffix: string = '') => {
            let spec = "yyyy-MM-dd"
            if (withTime) {
                spec += "'T'HH:mm:ss" + precisionSuffix('S')
                if (timeZoneSuffix.length) {
                    spec += timeZoneSuffix
                }
            }
            return format(date, spec, {timeZone: getTimeZone()})
        }

        const emitFormattedDate = (date: Date | null): void => {
            if (date !== null) {
                switch (props.modelFormat) {
                    case 'js-date':
                        return emit('update:model-value', date)
                    case 'str-date':
                        return emit('update:model-value', dateTimeString(date, false))
                    case 'str-datetime':
                        return emit('update:model-value', dateTimeString(date, true))
                    case 'str-datetime-tz':
                        return props.stripTz
                            ? emit('update:model-value', dateTimeString(date, true, "'Z'"))
                            : emit('update:model-value', dateTimeString(date, true, "XXX"))
                }
            }
            emit('update:model-value', null)
        }

        const getTimeZone = (): string => {
            return Intl.DateTimeFormat()
                .resolvedOptions()
                .timeZone
        }

        const handleModelValue = (newValue: ValidModelValue) => {
            modelFormatError.value = ''

            const date: Date | null = dateFromModel(newValue)

            if (date !== null && isDate(date) && !isValid(date)) {
                modelFormatError.value = `invalid model-value format: "${newValue}"`
            }

            internalDateObj.value = date
            internalDateText.value = (isDate(date) && isValid(date))
                ? intlFormat(date as Date,
                    {year: 'numeric', month: '2-digit', day: '2-digit', hour12: false},
                    {locale: props.locale})
                : ''
        }

        const parseLocaleDateStr = (date: string): Date => {
            return date.length == localeDateParsePattern.value.length
                ? parse(date, localeDateParsePattern.value, new Date(dateTimeEpoch)) ?? new Date('invalid')
                : new Date('invalid')
        }

        const precisionSuffix = (char: string): string => {
            return props.precision > 0
                ? '.' + char.repeat(props.precision)
                : ''
        }

        const reset = () => {
            emitFormattedDate(null)
        }

        const stripTz = (subject: string): string => {
            return subject.slice(0, 19 + (props.precision > 0 ? (props.precision + 1) : 0))
        }

        const uid = () => {
            return String(
                Date.now().toString(32) +
                Math.random().toString(16)
            ).replace(/\./g, '')
        }

        watch(internalDateObj, (newValue: any) => {
            showDatePicker.value = false
            emitFormattedDate(newValue)
        })

        watch(internalDateText, (newValue: any) => {
            if (newValue !== '' && newValue !== null) {
                const parsed = parseLocaleDateStr(newValue)
                if (isValid(parsed)) {
                    emitFormattedDate(parsed)
                }
            }
        })

        watch(() => props.modelValue, (newValue: ValidModelValue) => {
            handleModelValue(newValue);
        })

        handleModelValue(props.modelValue)
        uuid.value = uid()

        return {
            // ref
            internalDateObj,
            internalDateText,
            modelFormatError,
            showDatePicker,
            uuid,
            // computed
            displayFormat,
            // => f
            dateTextValidation,
            reset,
        }
    },
})
</script>

<style lang="scss">
.k-date-picker {
    .v-overlay__content {
        .v-picker__body > .v-date-picker-controls {
            .v-date-picker-controls__date + button {
                display: none;
            }
        }
    }
}
</style>

<style lang="scss" scoped>
.k-date-picker {
    ::v-deep(.v-date-picker) {
        .v-date-picker__input {
            padding: 0;

            .v-input__details {
                display: none;
            }
        }
    }
}
</style>
